<script lang="ts">
  import type { HTMLAttributes } from 'vue'
  import type { DateCalendarProps } from '../../date-calendar'
  import type { DatePickerAnchorTime } from '../../date-input'

  /** Props for `InputGroupDatePicker`. */
  export interface InputGroupDatePickerProps {
    /** `id` for the trigger — wire to a `<label for>` for a11y. */
    id?: string
    /** Text shown when no date is selected. */
    placeholder?: string
    disabled?: boolean
    /** Renders the destructive error ring via the parent `InputGroup`. */
    ariaInvalid?: boolean
    /**
     * Time-of-day a picked date is anchored to. `'noon'` keeps the value
     * on the same calendar day across timezones. Defaults to `'noon'`.
     */
    anchorTime?: DatePickerAnchorTime
    /** Date ranges to disable in the calendar. */
    disabledDateRanges?: DateCalendarProps['disabledRanges']
    /** Individual dates to disable in the calendar. */
    disabledDates?: DateCalendarProps['disabledDates']
    dataTestid?: string
    /** Class forwarded to the trigger button. */
    class?: HTMLAttributes['class']
  }
</script>

<script setup lang="ts">
  import { computed, inject, ref } from 'vue'
  import type { ISODateTimeString } from '@gat-ship/types'
  import { IconChevronDown } from '@gat-ship/icons'
  import { Popover, PopoverContent, PopoverTrigger } from '../popover'
  import { DateCalendar } from '../../date-calendar'
  import { useDatePicker } from '../../date-input'
  import { useUserPreferences } from '../../../composables/useUserPreferences'
  import { FIELD_CONTEXT_KEY } from '../field'
  import { IconCalendar } from '@gat-ship/icons'
  import { cn } from '../../../lib/utils'

  /**
   * A calendar date picker sized to slot into a `FormField` / `InputGroup`
   * exactly like `InputGroupInput`. It brings NO border of its own — the
   * surrounding `InputGroup` owns the chrome, and the control participates in
   * the parent's focus / error ring via the `data-slot="input-group-control"`
   * + `aria-invalid` hooks on the trigger button.
   *
   * The borderless, input-group-native sibling of `DatePicker` (which is a
   * standalone, self-bordered control). Unlike `InputGroupDate`, there is no
   * text input — the user cannot free-type a date; the calendar popover is the
   * only way in.
   *
   * Usage (inside a FormField, like any other input-group control):
   * ```vue
   * <FormField :form="form" name="grantFrom" label="Grant From" required>
   *   <template #default="{ field, invalid }">
   *     <InputGroupDatePicker
   *       :id="field.name"
   *       :model-value="field.state.value || undefined"
   *       :aria-invalid="invalid"
   *       @update:model-value="(v) => field.handleChange(v ?? '')"
   *       @blur="field.handleBlur"
   *     />
   *   </template>
   * </FormField>
   * ```
   */
  const props = withDefaults(defineProps<InputGroupDatePickerProps>(), {
    anchorTime: 'noon',
    placeholder: 'Pick a date'
  })

  const model = defineModel<ISODateTimeString | undefined>()

  const emit = defineEmits<{
    (e: 'blur'): void
  }>()

  const open = ref(false)

  const getUserPreferences = useUserPreferences()

  const locale = computed(
    () =>
      getUserPreferences().dateFormat ??
      Intl.DateTimeFormat().resolvedOptions().locale
  )

  const { selectedDate, handleDateChange } = useDatePicker(
    {
      modelValue: model,
      timeZone: undefined,
      anchorTime: () => props.anchorTime
    },
    (value) => {
      model.value = value
    }
  )

  /** The selected date in the user's locale format, or `''` when empty. */
  const displayValue = computed(() => {
    const date = selectedDate.value
    if (!date) return ''
    return date
      .toLocaleString(locale.value, {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      })
      .replace(',', '')
  })

  // Mirror the stacked-field height treatment that sibling input-group
  // controls get, so the trigger stays flush with neighbouring fields.
  const fieldContext = inject(FIELD_CONTEXT_KEY, {
    variant: 'default' as const
  })
  const stacked = computed(() => fieldContext.variant === 'stacked')

  // Closing the popover (pick or click-away) is the field's blur.
  function onOpenChange(value: boolean) {
    open.value = value
    if (!value) emit('blur')
  }

  // Commit the pick, then close through `onOpenChange` so blur fires
  // deterministically rather than depending on the popover re-emitting.
  function onCalendarChange(date: Parameters<typeof handleDateChange>[0]) {
    handleDateChange(date)
    onOpenChange(false)
  }
</script>

<template>
  <Popover :open="open" @update:open="onOpenChange">
    <PopoverTrigger as-child>
      <button
        :id="id"
        type="button"
        data-slot="input-group-control"
        :disabled="disabled"
        :data-testid="dataTestid"
        :aria-invalid="ariaInvalid || undefined"
        :class="
          cn(
            'flex w-full flex-1 items-center gap-2 rounded-none border-0 bg-transparent px-3 text-left text-sm outline-none focus-visible:ring-0 disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50',
            stacked ? 'h-auto py-0' : 'h-9',
            props.class
          )
        "
      >
        <IconCalendar class="text-muted-foreground size-4 shrink-0" />
        <span :class="displayValue ? '' : 'text-muted-foreground'">
          {{ displayValue || placeholder }}
        </span>
        <IconChevronDown
          class="text-muted-foreground ml-auto size-4 shrink-0 opacity-50"
        />
      </button>
    </PopoverTrigger>
    <PopoverContent as-child align="start">
      <DateCalendar
        mode="single"
        :model-value="selectedDate"
        :disabled-dates="disabledDates"
        :disabled-ranges="disabledDateRanges"
        @update:model-value="onCalendarChange"
      />
    </PopoverContent>
  </Popover>
</template>
